import { Alert, Form, Input, Spin } from "antd";
import { useForm } from "antd/es/form/Form";
import { useEffect, useState } from "react";
import { API_METHODS, BLANK_VALUE } from "../../../constants";
import useApi from "../../../hooks/useApi";
import { isEmpty } from "../../../generalFunctions";
import Footer from "./Footer";
import { useMediaQuery } from "react-responsive";
import heroContactUs from "../../../assets/img/hero-contactus.png";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import { Helmet } from "react-helmet";

const ContactUs = ({ showBanner }) => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState();
  const [response, request] = useApi(
    "/website/home/enquiry",
    null,
    API_METHODS.POST
  );

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setIsSuccess(null);
        setLoading(true);
        request(values);
      })
      .catch((_err) => {});
  };

  useEffect(() => {
    if (!isEmpty(response.data) && !response.isLoading && !response.error) {
      setLoading(false);
      setIsSuccess("sent");
      form.resetFields();
    } else if (
      !isEmpty(response.error) &&
      !response.isLoading &&
      !response.data
    ) {
      setIsSuccess("failed");
      setLoading(false);
    }
  }, [response]);

  const [getSeoDataResponse, getSeoDataRequest] = useApi(
    `/website/static-seo?page_identifier=Contact`,
    null,
    API_METHODS.GET
  );

  const [seoData, setSeoData] = useState(null);
  useEffect(() => {
    if (showBanner) {
      getSeoDataRequest();
    }
  }, []);

  useEffect(() => {
    if (getSeoDataResponse.data && !getSeoDataResponse.isLoading) {
      const data =
        getSeoDataResponse.data !== BLANK_VALUE
          ? getSeoDataResponse.data
          : null;
      setSeoData(data);
    }
  }, [getSeoDataResponse]);

  // SEO Helper Functions
  const generateOrganizationSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Cenvance Innovations Ltd",
      url: "https://cenvance.com",
      logo: "https://cenvance.com/logo.png",
      description:
        "We provide high-quality solutions that empower research laboratories, hospitals, and diagnostic centers worldwide.",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        email: "info@cenvance.com",
        areaServed: "Worldwide",
      },
    };
  };

  const generateContactPageSchema = () => {
    if (!seoData) return null;

    return {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: seoData.page_identifier,
      description: seoData.meta_description,
      url: "https://cenvance.com/contact",
      inLanguage: "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: "Cenvance Innovations Ltd",
        url: "https://cenvance.com",
      },
      keywords: seoData.meta_keywords,
    };
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData?.meta_title}</title>
        <meta
          name="description"
          content={
            seoData?.meta_description ||
            "Get in touch with Cenvance Innovations Ltd. We'd love to hear from you about our laboratory equipment and medical devices."
          }
        />
        {seoData?.meta_keywords && (
          <meta name="keywords" content={seoData.meta_keywords} />
        )}
        <meta name="robots" content={seoData?.meta_robots || "index, follow"} />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href={seoData?.canonical_url || "https://cenvance.com/contact"}
        />

        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cenvance Innovations Ltd" />
        <meta
          property="og:url"
          content={seoData?.canonical_url || "https://cenvance.com/contact"}
        />
        <meta
          property="og:title"
          content={seoData?.og_title || seoData?.meta_title}
        />
        <meta
          property="og:description"
          content={
            seoData?.og_description ||
            seoData?.meta_description ||
            "Get in touch with Cenvance Innovations Ltd"
          }
        />
        {seoData?.og_image && (
          <meta property="og:image" content={seoData.og_image} />
        )}

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={seoData?.twitter_title || seoData?.meta_title}
        />
        <meta
          name="twitter:description"
          content={
            seoData?.twitter_description ||
            seoData?.meta_description ||
            "Get in touch with Cenvance Innovations Ltd"
          }
        />
        {seoData?.twitter_image && (
          <meta name="twitter:image" content={seoData.twitter_image} />
        )}

        {/* Structured Data Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(generateOrganizationSchema())}
        </script>

        {seoData && (
          <script type="application/ld+json">
            {JSON.stringify(generateContactPageSchema())}
          </script>
        )}

        {/* Additional Schema if exists */}
        {seoData?.additional_schema && (
          <script type="application/ld+json">
            {typeof seoData.additional_schema === "string"
              ? seoData.additional_schema
              : JSON.stringify(seoData.additional_schema)}
          </script>
        )}
      </Helmet>
      {showBanner && (
        <div className="hero-banner">
          <img
            alt="Hero"
            src={heroContactUs}
            style={{
              height: "auto",
              width: "100%",
              maxHeight: isMobile ? "200px" : "auto",
              objectFit: "cover",
            }}
          />
          <Breadcrumbs
            items={[
              {
                title: "Contact Us",
              },
            ]}
          />
        </div>
      )}

      <div className="contact-us-page">
        <div className="contact-us-container">
          <Spin spinning={loading}>
            <div className="contact-form-card">
              {/* Header Section */}
              <div className="contact-header">
                <h1 className="contact-title">We'd Love to Hear From You</h1>
                <p className="contact-subtitle">
                  Fill out the form below and we'll get back to you as soon as
                  possible.
                </p>
              </div>

              {/* Form Section */}
              <div className="contact-form-container">
                <Form form={form} layout="vertical">
                  {/* First Row - Name and Email */}
                  <div className="contact-form-row">
                    <Form.Item
                      name="name"
                      className="contact-form-item"
                      rules={[
                        { required: true, message: "Please enter your name" },
                      ]}
                    >
                      <Input
                        placeholder="Your Name"
                        maxLength={100}
                        size={isMobile ? "large" : "middle"}
                      />
                    </Form.Item>

                    {!isMobile && (
                      <Form.Item
                        name="email"
                        className="contact-form-item"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your email",
                          },
                          {
                            type: "email",
                            message: "Please enter a valid email",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Email"
                          maxLength={50}
                          size="middle"
                        />
                      </Form.Item>
                    )}
                  </div>

                  {/* Email Row for Mobile */}
                  {isMobile && (
                    <div className="contact-form-row">
                      <Form.Item
                        name="email"
                        className="contact-form-item"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your email",
                          },
                          {
                            type: "email",
                            message: "Please enter a valid email",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Email"
                          maxLength={50}
                          size="large"
                        />
                      </Form.Item>
                    </div>
                  )}

                  {/* Second Row - Phone and Country */}
                  <div className="contact-form-row">
                    <Form.Item
                      name="phone"
                      className="contact-form-item"
                      rules={[
                        {
                          required: true,
                          message: "Please enter your phone number",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Phone"
                        maxLength={15}
                        size={isMobile ? "large" : "middle"}
                      />
                    </Form.Item>

                    {!isMobile && (
                      <Form.Item
                        name="country"
                        className="contact-form-item"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your country",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Country"
                          maxLength={20}
                          size="middle"
                        />
                      </Form.Item>
                    )}
                  </div>

                  {/* Country Row for Mobile */}
                  {isMobile && (
                    <div className="contact-form-row">
                      <Form.Item
                        name="country"
                        className="contact-form-item"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your country",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Country"
                          maxLength={20}
                          size="large"
                        />
                      </Form.Item>
                    </div>
                  )}

                  {/* Message Row */}
                  <Form.Item
                    name="message"
                    className="contact-message-item"
                    rules={[
                      { required: true, message: "Please enter your message" },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="Your Message"
                      maxLength={200}
                      rows={isMobile ? 6 : 4}
                      showCount
                      size={isMobile ? "large" : "middle"}
                    />
                  </Form.Item>

                  {/* Alert Messages */}
                  {isSuccess === "sent" && (
                    <Alert
                      type="success"
                      showIcon
                      closable
                      message="Enquiry sent successfully."
                      className="contact-alert"
                    />
                  )}
                  {isSuccess === "failed" && (
                    <Alert
                      type="error"
                      showIcon
                      closable
                      message="Unable to send enquiry. Please try again."
                      className="contact-alert"
                    />
                  )}

                  {/* Submit Button */}
                  <div className="submit-button-container">
                    <button
                      type="button"
                      className={`contact-submit-button ${
                        isMobile ? "mobile-button" : ""
                      }`}
                      onClick={onSubmit}
                      disabled={loading}
                    >
                      {loading ? "SUBMITTING..." : "SUBMIT"}
                    </button>
                  </div>
                </Form>
              </div>
            </div>
          </Spin>
        </div>
      </div>
      <Footer isTabletOrMobile={isTabletOrMobile} />
    </>
  );
};

export default ContactUs;
